// Keeping the guess area above the on-screen keyboard.
//
// **What the browser does on its own is wrong in both directions.** Left alone,
// iOS Safari scrolls when a field takes focus whether or not it needs to, and it
// aims to show the *field* — so the suggestion list and the guesses counter
// underneath end up behind the keyboard. Holding the page perfectly still was
// tried and is the same mistake the other way round: the header stayed, and the
// list and the counter were still under the keyboard, because this screen is
// built to end level with the fold and so has no scroll of its own to give.
//
// **What this does.** When the keyboard opens it works out the smallest scroll
// that keeps the caller's region above the keyboard, and moves the page there
// once. Smallest, so what is above stays on screen as far as it can; the header
// is allowed off the top if that is what it takes. Usually the answer is not to
// move at all.
//
// **The caller says how much room it needs, and asks for as little as it can
// live with.** Call Sheet asks for the search box and two suggestion rows, not
// three — it would rather show fewer rows than shove the page about, and the
// list it shows is then fitted to whatever room there turned out to be. Asking
// for the maximum was what made the page jump 110px on a real phone to reserve
// space for rows that could simply have been scrolled to.
//
// **Whatever is reserved is reserved before it exists**, which is why it moves
// at most once. Measuring only what is on screen meant the page sat still as the
// keyboard opened on an empty box and then jumped the moment a first letter
// brought the list up.
//
// **Nothing is decided until the keyboard has stopped moving.** iOS opens it over
// a quarter of a second and fires a viewport event on the way, each one with a
// part-open keyboard. Deciding on those meant deciding against a keyboard that
// was not there yet, and then correcting again — visible as the page stepping.
// Every event now only restarts a short quiet timer, and the single decision is
// made when the events stop.
//
// **The position it measures from is where the page was when the box was
// touched**, recorded on the pointer going down, before focus and before the
// browser has had a chance to move anything. Taking it later means taking it
// from wherever Safari has already scrolled to, and then "leave the page where
// it was" means leaving it where Safari put it.
//
// ---
//
// **Correcting afterwards cannot work, and a readout on a real iPhone is what
// proved it.** Safari does not scroll the document to reveal a focused field
// here; it *pans the visual viewport*, centring the field in whatever the
// keyboard leaves. Measured on an iPhone 15 Pro Max: `scrollY` 0, "landed 0",
// and the page visibly shifted — because the shift was `offsetTop`, which is
// about 110px on a first tap and a stubborn 7px afterwards. **No amount of
// `scrollTo` can put that back**, because it is not a scroll.
//
// So the point is not to correct the pan but to leave Safari no reason to pan,
// and that is two things:
//
// 1. **Give the document somewhere to scroll, before the field takes focus.**
//    Safari pans the visual viewport chiefly when it cannot scroll the document
//    instead, and this screen is built to end exactly level with the fold — so
//    there is never anywhere to scroll, and panning is the only move it has. The
//    foot padding was already being added, but only once the keyboard had been
//    measured, which is far too late to affect the decision. It now goes on as
//    the finger goes down, sized from the last keyboard this device showed.
// 2. **Take the tap over, where that costs nothing.** Focusing from script with
//    `preventScroll` tells Safari not to reveal the field at all. It means
//    preventing the default tap, which is also what places the caret inside
//    existing text — so it is done **only when the box is empty**, where there
//    is no caret to place and the tap has nothing else to do. A box with text in
//    it keeps the browser's own behaviour exactly.
//
// **Both worked.** Confirmed on an iPhone 15 Pro Max: `scrollY` 0 and
// `visualViewport.offsetTop` 0 on the first tap, while typing, after each guess
// and on the result screen. Neither could be checked here — no browser pane
// opens a real keyboard — so it was settled with a readout pinned to the screen
// and a screenshot, which is the only way this particular rule can ever be
// judged.
//
// **Everything is measured against `visualViewport`**, never the layout height.
// The layout viewport does not change when the keyboard opens; the visual one
// shrinks to what is actually on screen, which is the only thing that knows
// where the keyboard and its accessory bar are.
//
// ---
//
// **A version of this jittered on a real iPhone, and it is worth recording why,
// because every one of the three mistakes looked like care at the time.**
//
// 1. **It listened for scroll and it scrolled.** `visualViewport`'s `scroll`
//    event called the correction, and the correction called `scrollTo`, which
//    fires `scroll`. The only brake was a 2px tolerance, and on iOS the
//    requirement itself moves by more than 2px when the page scrolls: Safari
//    adjusts `visualViewport.offsetTop` as it pans, which shifts the boundary
//    being solved for. So the page chased a target that moved because it was
//    being chased. **Nothing here listens to scroll any more**, which is what
//    makes a self-inflicted loop impossible rather than merely damped.
// 2. **It corrected on every frame for 700ms**, on the reasoning that Safari
//    moves the page over several frames. That turned the exchange above into a
//    60-per-second oscillation, and it ran whether or not anything had changed.
//    **There is no timer and no animation-frame loop now:** the page is
//    adjusted once per real event, and there are only two such events "—" the
//    keyboard opening and the keyboard closing.
// 3. **It wrote the foot padding on every pass.** Padding the body is a layout
//    change, so it was reflowing the page 60 times a second to arrive at the
//    same number. **It is now written once when the keyboard opens and cleared
//    once when it closes.**
//
// And the reason it fought a finger: a drag fires those same viewport events, so
// every correction was an argument with the person holding the phone.
// **Nothing is adjusted while a finger is down** — a change that arrives mid-drag
// waits for the lift.

//
// The arithmetic is separated from the wiring so it can be tested: no headless
// browser opens a real keyboard, and a keyboard rule nothing exercises is a rule
// that rots.

export interface Fit {
  /** Document Y of the bottom of the area that has to stay visible. */
  mustSeeBottom: number;
  /** Distance from the top of the layout viewport to the bottom of the visible
   *  area: `visualViewport.offsetTop + visualViewport.height`. With the keyboard
   *  open, that is the top of the keyboard. */
  viewportBottom: number;
  /** Breathing room between the area and the keyboard. */
  margin: number;
}

/**
 * The smallest scroll position that puts `mustSeeBottom` above the keyboard.
 * Zero when it is already clear, so the page sits where it normally would.
 */
export function minimumScroll({ mustSeeBottom, viewportBottom, margin }: Fit): number {
  return Math.max(0, mustSeeBottom + margin - viewportBottom);
}

/**
 * Whether the page is far enough from where it should be to be worth moving.
 *
 * **A page already in the right place is never touched.** This is the whole
 * defence against a correction that corrects nothing: every event asks the
 * question, and almost every event is answered no.
 */
export function shouldMove(at: number, need: number, slack: number): boolean {
  return Math.abs(at - need) > slack;
}

/** How far the page may sit off `minimumScroll` before it is moved. */
const SLACK = 2;
/**
 * Breathing room between the guess area and the top of the keyboard.
 *
 * Small on purpose. It is one of only two things standing between two whole
 * suggestion rows and a page that has to shift to show them: measured on an
 * iPhone 15 Pro Max there are 81.5px below the search box, two rows and their
 * borders want 73.3, and what is left over has to cover this and the gap above
 * the list. At 8 it did not fit and the page moved 7px every time. The
 * keyboard's own accessory bar is a visual separator in its own right, so the
 * clearance is doing less work here than the number suggests.
 */
export const MARGIN = 2;
/**
 * How much of the screen has to go before it counts as a keyboard rather than
 * a browser bar. An iPhone keyboard with its accessory row takes well over 300px;
 * the address bar alone is nearer 50.
 */
const KEYBOARD_MIN = 120;
/**
 * How long the viewport has to hold still before the keyboard counts as having
 * finished opening. iOS animates it over about a quarter of a second and fires
 * events throughout; this is what stops a decision being taken against a
 * half-open keyboard and then taken again.
 */
const QUIET_MS = 150;
/**
 * What to assume a keyboard will take, before one has been seen on this device.
 * It only sizes the padding that gives the document room to scroll, and padding
 * below the footer costs nothing if it is too generous.
 */
const KEYBOARD_GUESS = 320;

/**
 * Whether this is the engine that pans the visual viewport to centre a focused
 * field. Sniffing an engine is normally the wrong move; here the workaround
 * exists for one engine's behaviour, and applying it anywhere else would change
 * behaviour that is already correct. iPadOS reports itself as a Mac, hence the
 * touch test.
 */
const PANS_TO_FOCUS = typeof navigator !== 'undefined' && (
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

export interface Options {
  /** Everything that has to stay above the keyboard. */
  region: () => HTMLElement | null;
  /** How much more, below the region, must also be clear. Asked for as the
   *  keyboard opens. */
  reserve?: () => number;
  /** Where the top of the visible area should be, in document coordinates, or
   *  null to use the smallest scroll that clears the keyboard. Where it gives a
   *  figure the page goes there as the keyboard opens and nowhere else after:
   *  the figure does not depend on the keyboard, the list or what is typed, so
   *  nothing that follows can produce a second, different answer. */
  anchor?: () => number | null;
  /** Called once the keyboard has settled and the page has been put where it is
   *  going, with whether the keyboard is up. The page has already moved by then,
   *  so a caller sizing itself to the room left over measures the position it
   *  will actually be in. */
  onSettle?: (keyboardOpen: boolean) => void;
}

/**
 * Keep `region` above the keyboard while `input` is focused. Returns a function
 * that undoes the wiring.
 *
 * Without `visualViewport` this does nothing: there is no way to know where the
 * keyboard is, and guessing is worse than leaving the browser to it — but
 * `onSettle` is still called once, so a caller that sizes itself to the keyboard
 * starts from a sensible state.
 */
/*
 * **A phone on its side is held to the same rules as one upright**, with one
 * difference its caller asks for: an `anchor`. Sideways the keyboard leaves so
 * little that the smallest scroll is not enough — the search box has to go as
 * high as it can, with the list under it — so the page is put at a fixed place
 * instead, once, as the keyboard opens.
 */

export function keepAboveKeyboard(input: HTMLElement, options: Options): () => void {
  const { region, reserve = () => 0, anchor = () => null, onSettle = () => {} } = options;
  const vv = window.visualViewport;
  if (!vv) { onSettle(false); return () => {}; }
  const view = vv;

  /** Whether the keyboard is currently up, as far as this module is concerned. */
  let open = false;
  /** The foot padding currently written, so it is written only when it changes. */
  let padded = 0;
  /** A finger is down. Nothing is adjusted until it lifts. */
  let touching = false;
  /** A real change arrived while a finger was down, and is owed an adjustment. */
  let deferred = false;
  /** Where the page sat when the box was touched, which is the position "leave
   *  it alone" means. Recorded before focus, so before anything can move it. */
  let from = 0;
  let haveFrom = false;
  /** The quiet timer: every viewport event restarts it, so the decision happens
   *  once the keyboard has stopped moving rather than during. */
  let quiet = 0;
  /** The last keyboard this device actually showed, to size the padding that
   *  goes on before the next one opens. */
  let lastKeyboard = 0;
  /** Padding was put on for a tap that has not yet produced a keyboard. */
  let early = false;
  /** The last position this module scrolled to, so it only takes back its own. */
  let ours: number | null = null;
  /** From the focus event. The active element is consulted as well, because a
   *  `focus` is not guaranteed to have been seen — a field can already hold
   *  focus when this is wired up. */
  let focusedFlag = false;

  const focused = () => focusedFlag || document.activeElement === input;

  /** The screen the keyboard has taken. `innerHeight` is the layout viewport,
   *  which does not change when the keyboard opens; the visual one does. */
  const keyboard = () => window.innerHeight - view.height;

  function pad(px: number): void {
    if (px === padded) return;
    padded = px;
    document.body.style.paddingBottom = px > 0 ? `${px}px` : '';
  }

  /** The scroll position that clears the keyboard, or null without a region. */
  function wanted(): number | null {
    const el = region();
    if (!el) return null;
    return minimumScroll({
      // A client rect is relative to the layout viewport, so adding the scroll
      // puts it into document coordinates — which is what it has to be in,
      // because the scroll is the thing being solved for. The reserve is the
      // room the list has not taken yet, so the answer is the same whether or
      // not anything is on screen under the input.
      mustSeeBottom: el.getBoundingClientRect().bottom + window.scrollY + reserve(),
      viewportBottom: view.offsetTop + view.height,
      margin: MARGIN
    });
  }

  /** Restart the quiet timer. Every viewport event comes through here, so the
   *  decision is made once, when they stop. */
  function schedule(): void {
    window.clearTimeout(quiet);
    quiet = window.setTimeout(decide, QUIET_MS);
  }

  function decide(): void {
    // Never fight a finger. Whatever changed is still true when it lifts.
    if (touching) { deferred = true; return; }
    deferred = false;

    const up = focused() && keyboard() > KEYBOARD_MIN;

    if (up) {
      open = true;
      early = false;
      lastKeyboard = keyboard();
      // Somewhere to scroll to. This screen ends level with the fold, so the
      // document is no taller than the viewport and asking for a scroll does
      // nothing; the padding sits below the footer, where nothing visible moves.
      // The keyboard's own height plus the reserve is always enough, because the
      // page fitted the screen before the keyboard took part of it away.
      pad(Math.max(padded, keyboard() + reserve() + MARGIN));

      // **An anchor, where the caller gives one, is where the page goes** — a
      // fixed place, not a shortfall, so a keyboard that grows a predictive row
      // or a list that opens produces the same answer and the page stays put.
      const anchored = anchor();
      const need = anchored ?? wanted();
      if (need === null) { onSettle(true); return; }

      // **Otherwise where it was, if that already clears the keyboard, or the
      // least that does.** Measuring from where the box was touched is what
      // makes this mean "leave the page alone" rather than "leave it wherever
      // the browser has put it" — and it is why the correction undoes a
      // scroll-into-view the browser did in the meantime.
      const target = anchored !== null ? Math.max(0, anchored) : (from >= need ? from : need);
      if (shouldMove(window.scrollY, target, SLACK)) {
        ours = target;
        window.scrollTo(0, target);
      }
      onSettle(true);
      return;
    }

    if (!open) {
      // A tap that never produced a keyboard: give the padding back — but not
      // while the field still holds focus, because a keyboard may simply not
      // have arrived yet, and taking the room away at that moment is taking it
      // away at exactly the moment it exists for.
      if (early && !focused()) { pad(0); early = false; }
      onSettle(false);
      return;
    }

    // **The keyboard has gone: the page goes back to exactly where it was when
    // the box was touched**, whatever moved it in between — this module, the
    // browser, or the browser's own adjustment as the keyboard closed. It used
    // to go back only if the page was still where this module had left it, on
    // the ground that a player might have scrolled deliberately; sideways that
    // left the page scrolled down with the header cut off after a title was
    // picked, because Safari had moved it a few pixels on the way out. Then the
    // padding goes, so no gap is left.
    open = false;
    if (haveFrom && shouldMove(window.scrollY, from, SLACK)) {
      window.scrollTo(0, from);
    }
    ours = null;
    pad(0);
    early = false;
    haveFrom = false;
    onSettle(false);
  }

  /**
   * The moment the box is touched: before focus, and before the browser has
   * moved anything.
   *
   * Two things happen here and the order matters. The page position is recorded,
   * because this is the only moment at which it is the player's rather than the
   * browser's. And **the document is given somewhere to scroll** — which is what
   * takes away Safari's reason to pan the visual viewport instead. The padding
   * sits below the footer, so nothing visible moves either way.
   */
  function mark(): void {
    if (!haveFrom) {
      from = window.scrollY;
      haveFrom = true;
    }
    // Only where the panning happens. On every other engine this padding would
    // make a page that fits the screen scrollable for no reason at all.
    if (PANS_TO_FOCUS) {
      const room = Math.max(lastKeyboard, KEYBOARD_GUESS) + reserve() + MARGIN;
      if (padded < room) { pad(room); early = true; }
    }
    // So that a tap which never produces a keyboard still gets tidied up.
    schedule();
  }

  /**
   * Take the tap over, so the field can be focused with `preventScroll`.
   *
   * **Only when the box is empty**, which is the one case where the default tap
   * has nothing to do that this does not: there is no caret to place. With text
   * in the box the browser keeps the tap, and tapping into the middle of a title
   * puts the caret where it is tapped, as it always did.
   *
   * If the takeover does not take — a WebKit version that focuses differently,
   * anything unforeseen — the field is focused the ordinary way on the next turn
   * of the loop, so a tap can never leave it dead.
   */
  function takeOver(event: PointerEvent): void {
    if (!PANS_TO_FOCUS) return;
    if (event.pointerType !== 'touch') return;
    if (document.activeElement === input) return;
    if ((input as HTMLInputElement).value !== '') return;
    event.preventDefault();
    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
    window.setTimeout(() => {
      if (document.activeElement !== input) input.focus();
    }, 0);
  }

  function onPointerDown(event: PointerEvent): void {
    mark();
    takeOver(event);
  }

  function onFocus(): void { focusedFlag = true; mark(); schedule(); }
  function onBlur(): void { focusedFlag = false; schedule(); }

  // The viewport really changing size is the keyboard opening, closing, or
  // growing a predictive-text row. It is not a scroll: scrolling does not change
  // `visualViewport.height`, which is why this is safe to act on and `scroll`
  // is not. It only restarts the timer; nothing is decided here.
  function onViewport(): void { schedule(); }

  // Coming back from another app, or from the lock screen, can restore the
  // keyboard without a viewport event of its own.
  function onVisible(): void { if (!document.hidden) schedule(); }

  function onTouchStart(): void { touching = true; }
  function onTouchEnd(): void {
    touching = false;
    if (deferred) schedule();
  }

  // Before focus, and before the browser scrolls anything into view: this is the
  // only moment at which "where the page was" is the player's position rather
  // than the browser's.
  input.addEventListener('pointerdown', onPointerDown);
  input.addEventListener('touchstart', mark, { passive: true });
  input.addEventListener('focus', onFocus);
  input.addEventListener('blur', onBlur);
  view.addEventListener('resize', onViewport);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('touchmove', onTouchStart, { passive: true });
  window.addEventListener('touchend', onTouchEnd, { passive: true });
  window.addEventListener('touchcancel', onTouchEnd, { passive: true });

  // Wired up while the field already holds focus — which happens on a hot
  // reload, and would otherwise leave the page uncorrected until the next tap.
  // Either way the caller is told where things stand before anything happens.
  if (document.activeElement === input) { mark(); schedule(); }
  else onSettle(false);

  return function stop(): void {
    window.clearTimeout(quiet);
    input.removeEventListener('pointerdown', onPointerDown);
    input.removeEventListener('touchstart', mark);
    input.removeEventListener('focus', onFocus);
    input.removeEventListener('blur', onBlur);
    view.removeEventListener('resize', onViewport);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchStart);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchcancel', onTouchEnd);
    open = false;
    ours = null;
    pad(0);
  };
}
